import React from 'react'
import classNames from 'classnames'

import { Pill } from '@/components/pills/Pill'

import { NODE_ICON_PATHS, NODE_TYPE_LABEL } from '@/views/VoiceAgents/canvasHelpers'
import DirectionPill from '@/views/VoiceAgents/components/DirectionPill'
import NodeChips from '@/views/VoiceAgents/components/NodeChips'

// Map node type → Pill variant prop. The variants line up with the design's
// color palette: green for call-start (entry), blue for agents, red for
// call-end (exit), etc. Falls through to gray (no variant prop) for any
// unknown type.
const TYPE_PILL_VARIANT = {
  entry: 'success',
  greeting: 'informative',
  prompt: 'contrast',
  subagent: 'informative',
  decision: 'warning',
  tool: 'contrast',
  exit: 'critical',
}

// Single node card on the canvas. Type-specific styling lives in SCSS via the
// `.voice-agents__canvas-node--{type}` modifier.
//
// Wrapped in React.memo so that selecting a node doesn't force every other
// node on the canvas to re-render (each one renders an inline SVG icon via
// dangerouslySetInnerHTML — cheap individually, expensive at 13+ nodes per
// click). The parent passes stable callbacks + `nodeId` so memoization holds.
const CanvasNode = React.memo(
  ({ node, nodeId, position, selected, onPointerDown, onClick, onChipClick }) => {
    const iconPath = NODE_ICON_PATHS[node.type] || ''
    const isMaster = node.type === 'subagent' && node.isMaster
    // Terminal nodes (entry / exit) render in a slim style — single
    // regular-weight title line with no desc, matching the design mock's
    // Call Start / Call End cards. The desc field stays on the fixture
    // for forward compat; the canvas just hides it for these node types.
    const isTerminal = node.type === 'entry' || node.type === 'exit'

    const handlePointerDown = (event) => onPointerDown(event, nodeId)
    const handleClick = () => onClick(nodeId)
    const handleKeyDown = (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onClick(nodeId)
      }
    }

    const typePillVariant = TYPE_PILL_VARIANT[node.type]
    const typePillProps = typePillVariant ? { [typePillVariant]: true } : {}
    const typeLabel = (NODE_TYPE_LABEL[node.type] || node.type).toUpperCase()

    return (
      <div
        className={classNames(
          'voice-agents__canvas-node',
          `voice-agents__canvas-node--${node.type}`,
          {
            'voice-agents__canvas-node--selected': selected,
            'voice-agents__canvas-node--master': isMaster,
          }
        )}
        data-testid={`canvas-node-${node.id}`}
        data-node-id={node.id}
        data-node-type={node.type}
        style={{ left: `${position.x}px`, top: `${position.y}px` }}
        onPointerDown={handlePointerDown}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <div className="voice-agents__canvas-node-head">
          <div
            className="voice-agents__canvas-node-avatar"
            aria-hidden="true"
            // SVG path is a static literal from canvasHelpers — safe to inline.
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">${iconPath}</svg>`,
            }}
          />
          <Pill xs {...typePillProps} className="voice-agents__canvas-node-type-pill">
            {typeLabel}
          </Pill>
        </div>
        <div
          className={classNames('voice-agents__canvas-node-title', {
            'voice-agents__canvas-node-title--terminal': isTerminal,
          })}
        >
          {node.title}
        </div>
        {!isTerminal && node.desc && (
          <div className="voice-agents__canvas-node-desc">{node.desc}</div>
        )}
        {node.type === 'entry' && (
          <div className="voice-agents__canvas-node-direction">
            <DirectionPill />
          </div>
        )}
        <NodeChips
          node={node}
          nodeId={nodeId}
          onChipClick={onChipClick}
          dataTestIdPrefix={`canvas-node-${node.id}`}
        />
      </div>
    )
  }
)

export default CanvasNode
